import type { Contact, CrmActivityDomain } from '@otw/models/leads/contacts';
import { resolveCrmActivityDomain } from '@otw/models/leads/contacts';
import type {
  ContactCompany,
  ContactCompanyActivity,
  ContactCompanyActivityType,
} from '@otw/models/leads/contactCompanies';
import type { Lead } from '@otw/models/leads/lead';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import type { CrmActivityDomainFilter } from './contactMergedTimeline';

export const COMPANY_ACTIVITY_TITLE: Record<ContactCompanyActivityType, string> = {
  call: 'Logged a call',
  sms: 'Logged SMS',
  email: 'Logged an email',
  note: 'Note',
  task: 'Created a task',
  lead: 'Lead created',
  shipping_repairs: 'Shipping & Repairs',
};

const normalizeCompanyKey = (name: string | undefined | null): string =>
  (name ?? '').trim().toLowerCase();

export type CompanyTimelineEntry =
  | { kind: 'created'; id: string; createdAt: Date; createdByUserId: string }
  | {
      kind: 'company-activity';
      id: string;
      createdAt: Date;
      activity: ContactCompanyActivity;
    }
  | {
      kind: 'contact-activity';
      id: string;
      createdAt: Date;
      type: ContactCompanyActivityType;
      note: string;
      domain?: CrmActivityDomain;
      contact: Contact;
      createdByUserId: string;
    };

export type CompanyActivityFilter = 'all' | ContactCompanyActivityType | 'created';

const activityTimelineKey = (
  prefix: string,
  activity: {
    type: string;
    createdAt: Date;
    note?: string;
    leadId?: string;
    taskId?: string;
    contactId?: string;
  },
): string =>
  [
    prefix,
    activity.createdAt.toISOString(),
    activity.type,
    activity.leadId ?? '',
    activity.taskId ?? '',
    activity.contactId ?? '',
    (activity.note ?? '').slice(0, 48),
  ].join('|');

const toDateMs = (value: Date | string | null | undefined): number => {
  if (!value) return -Infinity;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? -Infinity : parsed.getTime();
};

export const companyTimelineEntryAuthorId = (
  entry: CompanyTimelineEntry,
): string | undefined => {
  if (entry.kind === 'created') return entry.createdByUserId;
  if (entry.kind === 'company-activity') return entry.activity.createdByUserId;
  return entry.createdByUserId;
};

export function getCompanyTimelineDomain(
  entry: CompanyTimelineEntry,
): CrmActivityDomain | null {
  if (entry.kind === 'created') return null;
  if (entry.kind === 'company-activity') {
    return resolveCrmActivityDomain(entry.activity.domain);
  }
  return resolveCrmActivityDomain(entry.domain);
}

export function buildCompanyTimelineEntries(input: {
  company: ContactCompany;
  memberContacts: Contact[];
  leads: Lead[];
  inventoryLeads?: InventoryLead[];
}): CompanyTimelineEntry[] {
  const { company, memberContacts, leads, inventoryLeads } = input;
  const currentCompanyKey = normalizeCompanyKey(company.name);
  const leadsById = new Map(leads.map((lead) => [lead.id, lead]));
  const inventoryLeadsById = new Map(
    (inventoryLeads ?? []).map((lead) => [lead.id, lead]),
  );

  const isLeadActivityForThisCompany = (leadId?: string, note?: string): boolean => {
    if (leadId) {
      const inventoryLead = inventoryLeadsById.get(leadId);
      if (inventoryLead) {
        return inventoryLead.companyId === company.id;
      }
      const lead = leadsById.get(leadId);
      if (lead) {
        if (lead.companyId && lead.companyId === company.id) return true;
        return normalizeCompanyKey(lead.company) === currentCompanyKey;
      }
    }
    const noteCompany = note?.split('·')[0]?.trim();
    if (noteCompany) {
      return normalizeCompanyKey(noteCompany) === currentCompanyKey;
    }
    return false;
  };

  const entries: CompanyTimelineEntry[] = [
    {
      kind: 'created',
      id: 'company-created',
      createdAt: company.createdAt,
      createdByUserId: company.createdByUserId,
    },
  ];

  for (const activity of company.activities ?? []) {
    if (activity.type !== 'lead') continue;
    if (!isLeadActivityForThisCompany(activity.leadId, activity.note)) continue;
    entries.push({
      kind: 'company-activity',
      id: activityTimelineKey('company', activity),
      createdAt: activity.createdAt,
      activity,
    });
  }

  for (const contact of memberContacts) {
    for (const activity of contact.activities ?? []) {
      if (activity.type === 'lead') continue;
      entries.push({
        kind: 'contact-activity',
        id: activityTimelineKey(`contact-${contact.id}`, activity),
        createdAt: activity.createdAt,
        type: activity.type as ContactCompanyActivityType,
        note: activity.note,
        domain: activity.domain,
        contact,
        createdByUserId: activity.createdByUserId,
      });
    }
  }

  return entries.sort((a, b) => toDateMs(b.createdAt) - toDateMs(a.createdAt));
}

export function filterCompanyTimelineEntries(
  entries: CompanyTimelineEntry[],
  search: string,
  filter: CompanyActivityFilter,
  domainFilter: CrmActivityDomainFilter = 'all',
): CompanyTimelineEntry[] {
  const q = search.trim().toLowerCase();
  return entries.filter((entry) => {
    if (domainFilter !== 'all') {
      const domain = getCompanyTimelineDomain(entry);
      if (domain === null || domain !== domainFilter) return false;
    }
    if (filter !== 'all') {
      if (filter === 'created') {
        if (entry.kind !== 'created') return false;
      } else if (entry.kind === 'created') {
        return false;
      } else if (entry.kind === 'company-activity' && entry.activity.type !== filter) {
        return false;
      } else if (entry.kind === 'contact-activity' && entry.type !== filter) {
        return false;
      }
    }
    if (!q) return true;
    if (entry.kind === 'created') return 'company created'.includes(q);
    if (entry.kind === 'company-activity') {
      return (
        entry.activity.note?.toLowerCase().includes(q) ||
        COMPANY_ACTIVITY_TITLE[entry.activity.type].toLowerCase().includes(q)
      );
    }
    return (
      entry.note?.toLowerCase().includes(q) ||
      COMPANY_ACTIVITY_TITLE[entry.type].toLowerCase().includes(q) ||
      entry.contact.name.toLowerCase().includes(q)
    );
  });
}

export function getCompanyTimelineDisplay(entry: CompanyTimelineEntry): {
  activityType: ContactCompanyActivityType | 'created';
  title: string;
  body: string | null;
  contactName: string | null;
} {
  if (entry.kind === 'created') {
    return {
      activityType: 'created',
      title: 'Company created',
      body: null,
      contactName: null,
    };
  }
  if (entry.kind === 'company-activity') {
    return {
      activityType: entry.activity.type,
      title: COMPANY_ACTIVITY_TITLE[entry.activity.type],
      body: entry.activity.note || null,
      contactName: null,
    };
  }
  const domain = resolveCrmActivityDomain(entry.domain);
  let title = COMPANY_ACTIVITY_TITLE[entry.type];
  if (domain === 'marketing' && (entry.note || '').toLowerCase().startsWith('unsubscribed')) {
    title = 'Unsubscribed from marketing';
  } else if (domain === 'marketing' && entry.type === 'email') {
    title = 'Campaign email sent';
  }
  return {
    activityType: entry.type,
    title,
    body: entry.note || null,
    contactName: entry.contact.name || null,
  };
}
